/**
 * App-wide runtime configuration, fetched once from `GET /config`.
 *
 * This is what replaced the build-time `REACT_APP_AI_ENABLED` env var. The CMS
 * ships to many clients and not all have an AI setup (a Gemini key + a running
 * analyze server), so AI has to be switchable per client — but an env var meant
 * a per-client rebuild to flip it. Now it's a row in the `configs` table,
 * toggled from the CMS Settings page, and it takes effect on reload.
 *
 * `enable_ai` sits alongside the existing feature flags (enable_ads,
 * enable_live, …) and is served by the same public config endpoint.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { getSettingsSubscription } from "../api/HttpApis";

const AppConfigContext = createContext({
  config: null,
  loading: true,
  aiEnabled: false,
  refreshConfig: () => {},
});

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const response = await getSettingsSubscription();
      // Same unwrapping the Settings page uses — the axios helper already
      // returns `response.data`, so the config object is one level in.
      setConfig(response?.data ?? null);
    } catch (error) {
      // Fail CLOSED. A config fetch that errors must not light up AI controls
      // the client can't actually use, so we leave config null => aiEnabled
      // false. Every other flag has its own default at the point of use.
      console.error("Failed to load app config:", error);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({
      config,
      loading,
      // False while loading too, so AI controls never flash on screen and then
      // vanish once the real value arrives.
      aiEnabled: Boolean(config?.enable_ai),
      refreshConfig: load,
    }),
    [config, loading, load]
  );

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};

/** Full config + loading state. */
export const useAppConfig = () => useContext(AppConfigContext);

/**
 * The AI master switch. Every AI surface in the CMS is gated on this.
 * Returns false while the config is still loading and if the fetch failed.
 */
export const useAiEnabled = () => useContext(AppConfigContext).aiEnabled;

export default AppConfigContext;
